import { AutoComplete } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { Editor } from 'primereact/editor';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabMenu } from 'primereact/tabmenu';
import { useEffect, useState } from 'react';

import { Column } from 'primereact/column';
import { InputSwitch } from 'primereact/inputswitch';
import { useEmployee } from '../../../context/employee';
import { useSite } from '../../../context/site';
import EmployeeMultiSelect from '../../employee-multi-select';
import ParentCreate from '../../parent/create';
import ParentSearchSelectChildren from '../../parent/search-select-children';
import ParentUpdate from '../../parent/update';
import ServiceTypeGroupDropdown from '../../service-type-group-dropdown';
import { GetEditorFormats } from '../../../shared';

function ServiceGroupCreate({ open, onClose }) {
    const { employee } = useEmployee();
    const { CreateServiceGroup, SearchPrograms } = useSite();

    const [cancelled, setCancelled] = useState(false);
    const [dateService, setDateService] = useState(new Date());
    const [serviceType, setServiceType] = useState('');
    const [program, setProgram] = useState('');
    const [filteredPrograms, setFilteredPrograms] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [parentsChildren, setParentsChildren] = useState([]);
    const [activityName, setActivityName] = useState('');
    const [durationHours, setDurationHours] = useState(0);
    const [durationMinutes, setDurationMinutes] = useState(0);
    const [familiesLiteracyEval, setFamiliesLiteracyEval] = useState(0);
    const [familiesLiteracyReadingMore, setFamiliesLiteracyReadingMore] = useState(0);
    const [familiesProgramEval, setFamiliesProgramEval] = useState(0);
    const [familiesProgramIncreasedKnowledge, setFamiliesProgramIncreasedKnowledge] = useState(0);
    const [unknownParents, setUnknownParents] = useState(0);
    const [unknownChildren, setUnknownChildren] = useState(0);
    const [notes, setNotes] = useState('');

    const [activeIndex, setActiveIndex] = useState(0);
    const [openAddParent, setOpenAddParent] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [processingError, setProcessingError] = useState('');

    const buttonDisabled = () => {
        if (!dateService) {
            return true;
        }

        if (!serviceType) {
            return true;
        }

        if (!program?._id) {
            return true;
        }

        if (employees.length === 0) {
            return true;
        }

        if (cancelled) {
            return false;
        }

        if (parentsChildren.length === 0) {
            return true;
        }

        return false;
    };

    const onCancel = () => {
        onClose({ status: 499, message: 'Canceled' });
    };

    const onCreate = () => {
        setProcessing(true);

        const createData = {
            cancelled,
            dateService,
            serviceType,
            program: program._id,
            employees: employees.map((e) => e._id),
            parentsChildren: parentsChildren.map((pc) => {
                return {
                    parent: pc.parent._id,
                    children: [...pc.children.map((c) => c._id)]
                };
            }),
            activityName,
            durationHours,
            durationMinutes,
            familiesLiteracyEval,
            familiesLiteracyReadingMore,
            familiesProgramEval,
            familiesProgramIncreasedKnowledge,
            unknownParents,
            unknownChildren,
            notes
        };

        CreateServiceGroup(createData, (res) => {
            if (res.status !== 200) {
                setProcessingError(res.message);
                setProcessing(false);
            } else {
                onClose(res);
            }
        });
    };

    const tabItems = [
        { label: 'Basic' },
        { label: `Parents (${parentsChildren.length})` },
        { label: 'Other' }
    ];

    const onTabChange = ({ index }) => {
        setActiveIndex(index);
    };

    const FooterContent = (
        <div>
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={onCancel}
                className="p-button-text"
            />
            <Button
                label="Create"
                disabled={buttonDisabled()}
                icon="pi pi-check"
                onClick={onCreate}
            />
        </div>
    );

    useEffect(() => {
        if (employee?._id) {
            setEmployees([employee._id]);
        }
    }, [employee]);

    return (
        <Dialog
            header="Create New Group Service"
            maximizable
            visible={open}
            onHide={onCancel}
            footer={FooterContent}
            className="w-9"
        >
            <TabMenu model={tabItems} activeIndex={activeIndex} onTabChange={onTabChange} />

            <ErrorMessage
                error={processingError}
                onClick={() => {
                    setProcessingError('');
                }}
            />

            <ProcessingMessage processing={processing} />

            {processing ? null : (
                <div className="mt-2">
                    {activeIndex !== 0 ? null : (
                        <TabBasic
                            cancelled={cancelled}
                            setCancelled={setCancelled}
                            dateService={dateService}
                            setDateService={setDateService}
                            serviceType={serviceType}
                            setServiceType={setServiceType}
                            program={program}
                            setProgram={setProgram}
                            filteredPrograms={filteredPrograms}
                            employees={employees}
                            setEmployees={setEmployees}
                            activityName={activityName}
                            setActivityName={setActivityName}
                            durationHours={durationHours}
                            setDurationHours={setDurationHours}
                            durationMinutes={durationMinutes}
                            setDurationMinutes={setDurationMinutes}
                            notes={notes}
                            setNotes={setNotes}
                            SearchPrograms={SearchPrograms}
                            setFilteredPrograms={setFilteredPrograms}
                        />
                    )}

                    {activeIndex !== 1 ? null : (
                        <TabParentsChildren
                            parentsChildren={parentsChildren}
                            setParentsChildren={setParentsChildren}
                            setOpenAddParent={setOpenAddParent}
                        />
                    )}

                    {activeIndex !== 2 ? null : (
                        <TabOther
                            familiesLiteracyEval={familiesLiteracyEval}
                            setFamiliesLiteracyEval={setFamiliesLiteracyEval}
                            familiesLiteracyReadingMore={familiesLiteracyReadingMore}
                            setFamiliesLiteracyReadingMore={setFamiliesLiteracyReadingMore}
                            familiesProgramEval={familiesProgramEval}
                            setFamiliesProgramEval={setFamiliesProgramEval}
                            familiesProgramIncreasedKnowledge={familiesProgramIncreasedKnowledge}
                            setFamiliesProgramIncreasedKnowledge={
                                setFamiliesProgramIncreasedKnowledge
                            }
                            unknownParents={unknownParents}
                            setUnknownParents={setUnknownParents}
                            unknownChildren={unknownChildren}
                            setUnknownChildren={setUnknownChildren}
                        />
                    )}
                </div>
            )}

            {!openAddParent ? null : (
                <ParentSearchSelectChildren
                    open={openAddParent}
                    parentsChildren={parentsChildren}
                    onClose={(res) => {
                        setOpenAddParent(false);

                        if (res) {
                            setParentsChildren((prev) => {
                                return [...prev, res];
                            });
                        }
                    }}
                />
            )}
        </Dialog>
    );
}

function ErrorMessage({ error, onClick }) {
    if (!error) {
        return null;
    }

    return <Message severity="error" content={error} onClick={onClick} />;
}

function ProcessingMessage({ processing }) {
    if (!processing) {
        return null;
    }

    return (
        <>
            <p>Creating group service...</p>

            <ProgressSpinner />
        </>
    );
}

function TabBasic({
    cancelled,
    setCancelled,
    dateService,
    setDateService,
    serviceType,
    setServiceType,
    program,
    setProgram,
    filteredPrograms,
    employees,
    setEmployees,
    activityName,
    setActivityName,
    durationHours,
    setDurationHours,
    durationMinutes,
    setDurationMinutes,
    notes,
    setNotes,
    SearchPrograms,
    setFilteredPrograms
}) {
    const onSearchProgram = (data) => {
        SearchPrograms({ query: data.query }, (res) => {
            setFilteredPrograms(res.data.programs);
        });
    };

    const programItemTemplate = (item) => {
        return (
            <div className="flex align-items-center">
                <div className={item.active ? '' : 'text-red-500'}>
                    {item.programName}, {item.siteCity}, {item.siteState.itemData.abbr}
                </div>
            </div>
        );
    };
    return (
        <>
            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="cancelledId" className="font-bold block mb-2">
                        Cancelled
                    </label>

                    <InputSwitch
                        inputId="cancelledId"
                        checked={cancelled}
                        onChange={(e) => {
                            setCancelled(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="dateServiceId" className="font-bold block mb-2">
                        Service Date
                    </label>

                    <Calendar
                        inputId="dateServiceId"
                        value={dateService}
                        onChange={(e) => setDateService(e.value)}
                        showIcon
                        showButtonBar
                        appendTo="self"
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="serviceTypeId" className="font-bold block mb-2">
                        Service Type
                    </label>

                    <ServiceTypeGroupDropdown
                        serviceTypeGroupId={serviceType}
                        onChange={(serviceTypeId) => {
                            setServiceType(serviceTypeId);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="serviceTypeId" className="font-bold block mb-2">
                        Program Location
                    </label>

                    <AutoComplete
                        field="programName"
                        value={program}
                        suggestions={filteredPrograms}
                        completeMethod={onSearchProgram}
                        onChange={(e) => {
                            setProgram(e.value);
                        }}
                        itemTemplate={programItemTemplate}
                    />
                </div>
                
                <div className="flex-auto">
                    <label htmlFor="employeeId" className="font-bold block mb-2">
                        Employees ({employees.length} selected)
                    </label>

                    <EmployeeMultiSelect
                        addCurrent={true}
                        onChange={(employeeIds) => setEmployees(employeeIds)}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="activityNameId" className="font-bold block mb-2">
                        Activity Name
                    </label>

                    <InputText
                        id="activityNameId"
                        value={activityName}
                        keyfilter={/^[a-zA-Z0-9 ]*$/}
                        maxLength={48}
                        onChange={(e) => {
                            setActivityName(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="durationHoursId" className="font-bold block mb-2">
                        Duration Hours
                    </label>

                    <InputText
                        id="durationHoursId"
                        onFocus={(e) => {
                            e.target.select();
                        }}
                        value={durationHours}
                        keyfilter={/^[0-9]*$/}
                        maxLength={10}
                        onChange={(e) => {
                            setDurationHours(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="durationMinutesId" className="font-bold block mb-2">
                        Duration Minutes
                    </label>

                    <InputText
                        id="durationMinutesId"
                        onFocus={(e) => {
                            e.target.select();
                        }}
                        value={durationMinutes}
                        keyfilter={/^[0-9]*$/}
                        maxLength={10}
                        onChange={(e) => {
                            setDurationMinutes(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="notesId" className="font-bold block mb-2">
                        Notes
                    </label>

                    <Editor
                        id="notesId"
                        value={notes}
                        headerTemplate={<GetEditorFormats />}
                        onTextChange={(e) => setNotes(e.htmlValue)}
                        style={{ height: '320px' }}
                    />
                </div>
            </div>
        </>
    );
}

function TabOther({
    familiesLiteracyEval,
    setFamiliesLiteracyEval,
    familiesLiteracyReadingMore,
    setFamiliesLiteracyReadingMore,
    familiesProgramEval,
    setFamiliesProgramEval,
    familiesProgramIncreasedKnowledge,
    setFamiliesProgramIncreasedKnowledge,
    unknownParents,
    setUnknownParents,
    unknownChildren,
    setUnknownChildren
}) {
    return (
        <>
            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="familiesLiteracyEvalId" className="font-bold block mb-2">
                        Families Literacy Eval
                    </label>

                    <InputText
                        id="familiesLiteracyEvalId"
                        onFocus={(e) => {
                            e.target.select();
                        }}
                        value={familiesLiteracyEval}
                        keyfilter={/^[0-9]*$/}
                        maxLength={10}
                        onChange={(e) => {
                            setFamiliesLiteracyEval(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="familiesLiteracyReadingMoreId" className="font-bold block mb-2">
                        Families Literacy Reading More
                    </label>

                    <InputText
                        id="familiesLiteracyReadingMoreId"
                        onFocus={(e) => {
                            e.target.select();
                        }}
                        value={familiesLiteracyReadingMore}
                        keyfilter={/^[0-9]*$/}
                        maxLength={10}
                        onChange={(e) => {
                            setFamiliesLiteracyReadingMore(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="familiesProgramEvalId" className="font-bold block mb-2">
                        Families Program Eval
                    </label>

                    <InputText
                        id="familiesProgramEvalId"
                        onFocus={(e) => {
                            e.target.select();
                        }}
                        value={familiesProgramEval}
                        keyfilter={/^[0-9]*$/}
                        maxLength={10}
                        onChange={(e) => {
                            setFamiliesProgramEval(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label
                        htmlFor="familiesProgramIncreasedKnowledgeId"
                        className="font-bold block mb-2"
                    >
                        Families Program Increased Knowledge
                    </label>

                    <InputText
                        id="familiesProgramIncreasedKnowledgeId"
                        onFocus={(e) => {
                            e.target.select();
                        }}
                        value={familiesProgramIncreasedKnowledge}
                        keyfilter={/^[0-9]*$/}
                        maxLength={10}
                        onChange={(e) => {
                            setFamiliesProgramIncreasedKnowledge(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="unknownParentsId" className="font-bold block mb-2">
                        Unknown Parents
                    </label>

                    <InputText
                        id="unknownParentsId"
                        onFocus={(e) => {
                            e.target.select();
                        }}
                        value={unknownParents}
                        keyfilter={/^[0-9]*$/}
                        maxLength={10}
                        onChange={(e) => {
                            setUnknownParents(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="unknownChildrenId" className="font-bold block mb-2">
                        Unknown Children
                    </label>

                    <InputText
                        id="unknownChildrenId"
                        onFocus={(e) => {
                            e.target.select();
                        }}
                        value={unknownChildren}
                        keyfilter={/^[0-9]*$/}
                        maxLength={10}
                        onChange={(e) => {
                            setUnknownChildren(e.target.value);
                        }}
                    />
                </div>
            </div>
        </>
    );
}

function TabParentsChildren({ parentsChildren, setParentsChildren, setOpenAddParent }) {
    const [openCreate, setOpenCreate] = useState(false);
    const [openUpdate, setOpenUpdate] = useState(false);
    const [updateData, setUpdateData] = useState({});

    const actionsDisplay = (data, { rowIndex }) => {
        const { parent } = data;

        return (
            <>
                <Button
                    icon="pi pi-times"
                    size="small"
                    rounded
                    severity="danger"
                    onClick={() => {
                        setParentsChildren((prev) => {
                            const newPc = [];

                            for (const pc of prev) {
                                const found = pc.parent._id === parent._id;

                                if (!found) {
                                    newPc.push(pc);
                                }
                            }

                            return newPc;
                        });
                    }}
                />

                <Button
                    icon="pi pi-user-edit"
                    className="ml-1"
                    title={`Edit ${parent.nameFirst} ${parent.nameLast}`}
                    size="small"
                    rounded
                    severity="help"
                    onClick={() => {
                        setUpdateData(parent);
                        setOpenUpdate(true);
                    }}
                />

                {!openUpdate ? null : (
                    <ParentUpdate
                        open={openUpdate}
                        data={updateData}
                        onClose={(res) => {
                            setUpdateData({});
                            setOpenUpdate(false);
                        }}
                    />
                )}
            </>
        );
    };

    const parentDisplay = (parentChildren) => {
        let sRes = '';

        for (const child of parentChildren.children) {
            if (sRes.length === 0) {
                sRes += `${child.nameFirst} (Age ${child.age})`;
            } else {
                sRes += `, ${child.nameFirst} (Age ${child.age})`;
            }
        }

        return sRes;
    };

    return (
        <>
            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <Button icon="pi pi-plus" rounded onClick={() => setOpenAddParent(true)} />
                </div>

                <div className="flex-auto">
                    <Button
                        icon="pi pi-user-plus"
                        severity="success"
                        rounded
                        onClick={() => setOpenCreate(true)}
                    />
                </div>
            </div>

            <Divider />

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <DataTable
                        value={parentsChildren}
                        showGridlines
                        paginator
                        removableSort
                        emptyMessage="No parents"
                        rows={5}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                    >
                        <Column field="parent.nameFilter" header="Parent" sortable></Column>
                        <Column body={parentDisplay} header="Children"></Column>
                        <Column
                            header="Actions"
                            bodyStyle={{ width: '150px' }}
                            body={actionsDisplay}
                        ></Column>
                    </DataTable>
                </div>
            </div>

            {!openCreate ? null : (
                <ParentCreate
                    open={openCreate}
                    onClose={() => {
                        setOpenCreate(false);
                    }}
                />
            )}
        </>
    );
}

export default ServiceGroupCreate;
